<template>
  <VFormFeedback :error="errors.name && errors.name[0]" :class="formFeedbackVariationClassName">
    <VInput v-model="name" :config="{ label: 'Source name', required: true }" class="slim" />
  </VFormFeedback>
  <div>
    <VBtn
      class="full-width slim"
      type="button"
      :loading="isLoading"
      :disabled="isSubmitDisabled"
      @click="onSubmit"
    >
      {{ submitText }}
    </VBtn>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType } from 'vue';
  import type { ICompanySourceModel, TCompanySource, TSourceErrors } from '~/shared/source/types';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import VFormFeedback from '~/misc/v-form-feedback';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';

  const props = defineProps({
    model: {
      type: Object as PropType<ICompanySourceModel>,
      required: true,
    },
    companyId: {
      type: Number,
      required: true,
    },
    source: {
      type: Object as PropType<TCompanySource | null>,
      default: null,
    },
    submitText: {
      type: String,
      default: 'Save',
    },
    formFeedbackVariationClassName: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits<{
    (e: 'upsert', source: TCompanySource): void;
  }>();

  const { notifyFailure } = useNotifyResource();

  const name: Ref<string> = ref<string>(props.source?.name ?? '');
  const errors: Ref<TSourceErrors> = ref<TSourceErrors>({});
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const isSubmitDisabled: ComputedRef<boolean> = computed<boolean>(() => {
    if (!name.value.trim()) return true;

    if (props.source && name.value.trim() === props.source.name) return true;

    return false;
  });

  const setName = (value: string): void => {
    name.value = value;
  };

  const setErrors = (value: TSourceErrors): void => {
    errors.value = value;
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setErrors({});

      const payload = { name: name.value.trim() };

      const response: TCompanySource = props.source
        ? await props.model.update(props.source.id, payload)
        : await props.model.create(props.companyId, payload);

      if (!props.source) {
        setName('');
      }

      emit('upsert', response);
    } catch (e: unknown) {
      const error = e as { response?: { data?: { errors?: TSourceErrors; message?: string } } };

      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error?.response?.data?.message) {
        notifyFailure(error.response.data.message);
      } else {
        notifyFailure();
      }
    } finally {
      setIsLoading(false);
    }
  };
</script>
